import React, { useEffect, useState } from 'react';
import FaiTypography from "../../MuiComponents/FaiTypography";
import FaiBox from "../../MuiComponents/FaiBox";
import { Stack } from "@mui/material";
import Icon from "@mui/material/Icon";
import HeaderBar, { FeedbackFilters } from './components/HeaderBar';
import ReportList from './components/CustomerFeedbackList';
import typography from '../../assets/theme/base/typography';
import ReviewController, { MerchantReview } from '../../controllers/reviewController';

const columns = [
    { Header: "Product", accessor: "productName" },
    { Header: "Product Id", accessor: "productId" },
    {
        Header: "Rating",
        accessor: "rating",
        Cell: ({ value }: { value: number }) => (
            <Stack direction="row" alignItems="center" spacing={0.5}>
                <FaiTypography variant="button" fontWeight={typography.fontWeightMedium}>{value}</FaiTypography>
                <Icon sx={{ color: "#FFB400", fontSize: "18px !important" }}>star</Icon>
            </Stack>
        ),
    },
    { Header: "Customer", accessor: "customerName" },
    { Header: "Date", accessor: "date" },
    { Header: "Review", accessor: "comment" },
];

const formatDate = (iso: string | null): string => {
    if (!iso) return '-';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

const CustomerFeedback: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<FeedbackFilters>({});

    useEffect(() => {
        const load = async () => {
            try {
                const res = await ReviewController.getMerchantReviews(0, 100);
                const reviews: MerchantReview[] = res.data?.data?.data ?? [];
                setRows(
                    reviews.map((r) => ({
                        productName: r.productName ?? '-',
                        productId: r.productId ?? '-',
                        rating: r.rating ?? 0,
                        customerName: r.customerName ?? 'Anonymous',
                        date: formatDate(r.createdAt),
                        comment: [r.headline, r.review].filter(Boolean).join(' — ') || '-',
                    }))
                );
            } catch (e) {
                setError('Could not load customer feedback.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const search = (filters.search ?? '').trim().toLowerCase();
    const shownRows = rows.filter((r) =>
        (!search || [r.productName, r.productId, r.customerName, r.comment].some((v) => String(v ?? '').toLowerCase().includes(search)))
        && (!filters.rating || Math.round(Number(r.rating)) === filters.rating)
    );

    return (
        <FaiBox className={"inventory"}>
            <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                Customer Feedback
            </FaiTypography>
            <FaiTypography variant={"h6"} fontWeight={typography.fontWeight} mb={"21px"}>
                The feedback which customers have submitted towards your products.
            </FaiTypography>

            <FaiBox className={"inventory-content"}>
                <FaiBox className={"inventory-content-header"}>
                    <HeaderBar filters={filters} onFilterChange={setFilters} />
                </FaiBox>
                <FaiBox className={"inventory-content-body"} mt={3}>
                    {loading ? (
                        <FaiTypography>Loading customer feedback...</FaiTypography>
                    ) : error ? (
                        <FaiTypography color="error">{error}</FaiTypography>
                    ) : rows.length === 0 ? (
                        <FaiTypography>No customer reviews yet for your products.</FaiTypography>
                    ) : shownRows.length === 0 ? (
                        <FaiTypography>No reviews match these filters.</FaiTypography>
                    ) : (
                        <ReportList data={{ columns, rows: shownRows }} />
                    )}
                </FaiBox>
            </FaiBox>
        </FaiBox>
    );
};

export default CustomerFeedback;
